import { keepPreviousData, useQuery } from '@tanstack/react-query'
import clsx from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import {
    LuAlertCircle,
    LuAlertTriangle,
    LuArrowLeft,
    LuArrowRight,
    LuCheckCircle,
    LuEqualNot,
    LuLoader2,
    LuLock
} from 'react-icons/lu'
import { Link } from 'react-router-dom'
import { useDebounce } from 'react-use'

import http from '../../../lib/http'
import { useAppSelector } from '../../../store'
import { StatusesEnum, useAdvancedSearchHook } from '../../advanced-search/advanced-search-hook'
import FriendlyDate from '../../shared/Date/FriendlyDate'
import { PageHeader } from '../components/page-header'
import { SearchInput } from '../components/search-input'
import * as constants from './constants'
import { DataComparisonProps, DataComparisonRow, MergeSummarySidebarProps, PatientMergeConstraintsResponse, PatientMergeJobResponse, PatientMergePageProps, PatientMergeSummaryResponse } from './types'
import * as utils from './utils'

const MergeSummarySidebar = (props: MergeSummarySidebarProps): React.ReactNode => {
    const handleBack = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault()

        if (props.onBack) {
            props.onBack()
        } else {
            window.history.back()
        }
    }

    return (
        <div className='flex flex-col rounded-xl bg-gray-50 p-5'>
            <p className='text-lg font-semibold text-gray-900'>Merge Summary</p>

            <div className='mt-4 flex flex-col gap-4'>
                {props.hasOpenEncounters && (
                    <div className='flex items-start gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700'>
                        <LuAlertCircle className='mt-0.5 h-4 w-4 shrink-0 text-red-600' aria-hidden />

                        <span>Open encounter in progress. Finish the encounter before merging.</span>
                    </div>
                )}

                {props.hasDraftIgaos && (
                    <div className='flex items-start gap-2 rounded-md bg-amber-100 px-3 py-2 text-sm text-amber-800'>
                        <LuAlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-amber-600' aria-hidden />

                        <span>Draft IGAO(s) found. Drafts will not be merged.</span>
                    </div>
                )}

                {props.hasOpenEncounters === false && (
                    <>
                        {props.mergeJob && (
                            <div className='rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-700'>
                                <p className='font-medium'>Job: {props.mergeJob.status}</p>

                                {props.mergeJob.processed_resources != null && props.mergeJob.total_resources != null && (
                                    <>
                                         {(props.mergeJob.total_resources === 0 && props.mergeJob.status !== 'completed') ? (
                                             <p className='mt-0.5 text-xs'>
                                                 Waiting for resources to be processed...
                                             </p>
                                         ) : (
                                             <p className='mt-0.5 text-xs'>
                                                 {props.mergeJob.processed_resources} / {props.mergeJob.total_resources} resources
                                             </p>
                                         )}
                                    </>
                                )}
                            </div>
                        )}

                        {!props.isMergeJobCompleted && (
                            <label
                                htmlFor='merge-confirm-checkbox'
                                className={clsx('mt-2 flex items-start gap-3', props.isMergeJobRunning ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer')}
                            >
                                <input
                                    id='merge-confirm-checkbox'
                                    type='checkbox'
                                    checked={props.mergeConfirmed}
                                    onChange={(e) => props.onMergeConfirmedChange(e.target.checked)}
                                    className='peer sr-only'
                                    disabled={props.isMergeJobRunning}
                                />

                                <span className='relative mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border-1 border-solid border-gray-500 bg-white transition-colors peer-checked:border-indigo-600 peer-checked:bg-indigo-600'>
                                    {props.mergeConfirmed && (
                                        <svg className='h-3 w-3 text-white' fill='none' viewBox='0 0 24 24' strokeWidth={3} stroke='currentColor' aria-hidden>
                                            <path strokeLinecap='round' strokeLinejoin='round' d='M5 13l4 4L19 7' />
                                        </svg>
                                    )}
                                </span>

                                <span className='text-sm text-gray-900'>
                                    I confirm that I have reviewed the data differences and approve this irreversible merge operation.
                                </span>
                            </label>
                        )}

                        {props.mergeStartError && (
                            <div className='flex items-start gap-2 rounded-md border border-solid border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700'>
                                <LuAlertCircle className='mt-0.5 h-4 w-4 shrink-0 text-red-600' aria-hidden />

                                <span>{props.mergeStartError}</span>
                            </div>
                        )}

                        {props.isMergeJobCompleted && (
                            <div className='flex items-center gap-2 rounded-md bg-green-100 px-3 py-2 text-sm text-green-700'>
                                <LuCheckCircle className='h-4 w-4 shrink-0 text-green-600' aria-hidden />

                                <span>Patient data has been successfully merged. The secondary patient record is preserved so you can verify that all information was transferred correctly. Once confirmed, you can delete it on the <Link to="/admin/patient-merge-history" className='underline text-green-700' onClick={handleBack}>Merge History page</Link>.</span>
                            </div>
                        )}

                        {!props.isMergeJobCompleted && (
                            <button
                                type='button'
                                onClick={props.onConfirmMerge}
                                disabled={props.disabled}
                                className='flex cursor-pointer justify-center items-center gap-2 mt-2 rounded-lg bg-indigo-600 px-4 py-3 font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50'
                            >
                                {props.isMergeJobRunning ? (
                                    <LuLoader2 className='h-4 w-4 shrink-0 animate-spin' aria-hidden />
                                ) : props.disabled ? (
                                    <LuLock className='h-4 w-4 shrink-0' aria-hidden />
                                ) : null}
                                Merge
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}

const getComparisonRows = (resolveClientName: (clientKey?: string | null) => string): DataComparisonRow[] => {
    return [
        {
            label: 'Full Name',
            getSecondary: (p) => `${(p.firstName ?? '').trim()} ${(p.lastName ?? '').trim()}`.trim() || constants.EMPTY_LABEL,
            getPrimary: (p) => `${(p.firstName ?? '').trim()} ${(p.lastName ?? '').trim()}`.trim() || constants.EMPTY_LABEL,
        },
        {
            label: 'Patient ID',
            getSecondary: (p) => (p.id ?? '').trim() || constants.EMPTY_LABEL,
            getPrimary: (p) => (p.id ?? '').trim() || constants.EMPTY_LABEL,
        },
        {
            label: 'Date of Birth',
            getSecondary: (p) => (p.birthDate ? String(p.birthDate) : constants.EMPTY_LABEL),
            getPrimary: (p) => (p.birthDate ? String(p.birthDate) : constants.EMPTY_LABEL),
        },
        {
            label: 'Status',
            getSecondary: (p) => (p.status ?? p.statusLabel ?? '').trim() || constants.EMPTY_LABEL,
            getPrimary: (p) => (p.status ?? p.statusLabel ?? '').trim() || constants.EMPTY_LABEL,
        },
        {
            label: 'Client',
            getSecondary: (p) => resolveClientName(p.clientName) || (p.clientName ?? p.clientId ?? '').trim() || constants.EMPTY_LABEL,
            getPrimary: (p) => resolveClientName(p.clientName) || (p.clientName ?? p.clientId ?? '').trim() || constants.EMPTY_LABEL,
        },
        {
            label: 'Member ID',
            getSecondary: (p) => (p.memberId ?? '').trim() || constants.EMPTY_LABEL,
            getPrimary: (p) => (p.memberId ?? '').trim() || constants.EMPTY_LABEL,
        },
        {
            label: 'Portal Member ID',
            getSecondary: (p) => (p.portalMemberId ?? '').trim() || constants.EMPTY_LABEL,
            getPrimary: (p) => (p.portalMemberId ?? '').trim() || constants.EMPTY_LABEL,
        },
        {
            label: 'Group Number',
            getSecondary: (p) => (p.groupNumber ?? '').trim() || constants.EMPTY_LABEL,
            getPrimary: (p) => (p.groupNumber ?? '').trim() || constants.EMPTY_LABEL,
        },
        {
            label: 'SSN',
            getSecondary: (p) => (p.ssn ?? '').trim() || constants.EMPTY_LABEL,
            getPrimary: (p) => (p.ssn ?? '').trim() || constants.EMPTY_LABEL,
        },
        {
            label: 'Phones',
            getSecondary: (p) => utils.formatListValue(p.phones, '') || constants.EMPTY_LABEL,
            getPrimary: (p) => utils.formatListValue(p.phones, '') || constants.EMPTY_LABEL,
        },
        {
            label: 'Last Encounter',
            getSecondary: (p) => (p.lastEncounter ? String(p.lastEncounter) : constants.EMPTY_LABEL),
            getPrimary: (p) => (p.lastEncounter ? String(p.lastEncounter) : constants.EMPTY_LABEL),
        },
        {
            label: 'Last Encounter Type',
            getSecondary: (p) => (p.lastType ?? '').trim() || constants.EMPTY_LABEL,
            getPrimary: (p) => (p.lastType ?? '').trim() || constants.EMPTY_LABEL,
        },
        {
            label: 'Created',
            getSecondary: (p) => (p.createdAt ? String(p.createdAt) : constants.EMPTY_LABEL),
            getPrimary: (p) => (p.createdAt ? String(p.createdAt) : constants.EMPTY_LABEL),
        },
        {
            label: 'Mobile Apps',
            getSecondary: (p) => utils.formatListValue(p.mobileApplicationsAccess, '') || constants.EMPTY_LABEL,
            getPrimary: (p) => utils.formatListValue(p.mobileApplicationsAccess, '') || constants.EMPTY_LABEL,
        }
    ]
}

const DataComparison = (props: DataComparisonProps): React.ReactNode => {
    const rows = getComparisonRows(props.resolveClientName)

    return (
        <div className='rounded-xl overflow-hidden border border-solid border-gray-200'>
            <div 
                className='py-3 px-4'
                // INFO: tailwind border class names don't work, so we use inline style
                style={{ borderBottom: '0.5px solid #E4E7EB' }}
            >
                <span className='text-lg font-medium text-gray-600'>Data Comparison</span>
            </div>

            <div className='flex flex-col gap-2 p-4'>
                <div
                    className='grid grid-cols-[160px_minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 px-3 pb-2 tracking-wide text-gray-600'
                    // INFO: tailwind border class names don't work, so we use inline style
                    style={{ borderBottom: '0.5px solid #E4E7EB' }}
                >
                    <span className='text-sm font-medium'>Field name</span>
                    <span className='text-sm font-medium'>Secondary value</span>
                    <div className='w-4 h-4' aria-hidden />
                    <span className='text-sm font-medium'>Primary value</span>
                </div>

                {rows.map((r) => {
                    const secondaryVal = r.getSecondary(props.mergePatient)
                    const primaryVal = r.getPrimary(props.mainPatient)

                    const isDiff = secondaryVal !== primaryVal

                    return (
                        <div
                            key={r.label}
                            className='grid grid-cols-[160px_minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 px-3 py-2.5 text-sm last:border-b-0'
                            // INFO: tailwind border class names don't work, so we use inline style
                            style={{ borderBottom: '0.5px solid #E4E7EB' }}
                        >
                            <div className='flex min-w-0 items-center gap-2'>
                                <span className='text-gray-600'>{r.label}</span>
                            </div>

                            <div
                                className={`min-w-0 truncate rounded px-2 py-1 ${isDiff ? 'bg-amber-50 text-amber-700' : 'text-gray-400'}`}
                                title={secondaryVal}
                            >
                                {secondaryVal === constants.EMPTY_LABEL ? <span className='text-gray-400'>{constants.EMPTY_LABEL}</span> : secondaryVal}
                            </div>

                            <div className='flex justify-center'>
                                {isDiff ? (
                                    <LuEqualNot className='h-4 w-4 shrink-0 text-gray-400' aria-hidden />
                                ) : null}
                            </div>

                            <div
                                className={`min-w-0 truncate rounded px-2 py-1 ${isDiff ? 'bg-green-50 text-green-700' : 'text-gray-400'}`}
                                title={primaryVal}
                            >
                                {primaryVal === constants.EMPTY_LABEL ? <span className='text-gray-400'>{constants.EMPTY_LABEL}</span> : primaryVal}
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export const PatientMergePage = (props: PatientMergePageProps): React.ReactNode => {
    const {clients} = useAppSelector(state => state.globalVars)
    const [searchValue, setSearchValue] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [mainPatientId, setMainPatientId] = useState<string | null>(null)
    const [mergePatientId, setMergePatientId] = useState<string | null>(null)
    const [mergeJobId, setMergeJobId] = useState<string | null>(null)
    const [mergeStartError, setMergeStartError] = useState<string | null>(null)
    const [mergeConfirmed, setMergeConfirmed] = useState(false)
    const [isMergeStarted, setIsMergeStarted] = useState(false)

    const patientMergePermission = useAppSelector(
        (state) => state?.globalVars?.permissions?.patient_merge ?? 'none'
    )

    const user = useAppSelector((state) => state?.globalVars?.user)

    useDebounce(() => {
        setDebouncedSearch(searchValue)
    }, 400, [searchValue])

    useEffect(() => {
        setMainPatientId(null)
        setMergePatientId(null)
        setMergeConfirmed(false)
        setMergeJobId(null)
        setMergeStartError(null)
    }, [searchValue])

    useEffect(() => {
        setMergeConfirmed(false)
        setMergeJobId(null)
        setMergeStartError(null)
    }, [mainPatientId, mergePatientId])

    const [patientSearchQuery] = useAdvancedSearchHook({
        anyName: debouncedSearch,
        scope: 'all',
        status: StatusesEnum.all,
        count: 25,
        page: 0,
        clientName: 'all',
        sortBy: 'lastName',
        sortDir: 'asc',
        managingOrg: 'all',
        patientId: undefined,
        memberId: undefined,
        phone: undefined,
        ssn: undefined,
        birthDate: undefined,
        opensearch: true
    }, {
        placeholderData: keepPreviousData,
        refetchOnMount: false,
        retry: false
    })

    const samePatientSelected = mainPatientId && mergePatientId && mainPatientId === mergePatientId

    const summaryQuery = useQuery({
        queryKey: ['patient-merge-summary', mainPatientId, mergePatientId],

        queryFn: async () => {
            return await http.getRequest<PatientMergeSummaryResponse>(
                '/admin/patient-merge/summary',
                {mainPatientId, mergePatientId},
                {clientName: false}
            )
        },

        enabled: Boolean(mainPatientId && mergePatientId && !samePatientSelected)
    })

    const constraintsQuery = useQuery({
        queryKey: ['patient-merge-constraints', mainPatientId, mergePatientId],

        queryFn: async () => {
            return await http.getRequest<PatientMergeConstraintsResponse>(
                '/admin/patient-merge/constraints',
                {mainPatientId, mergePatientId},
                {clientName: false}
            )
        },

        enabled: Boolean(mainPatientId && mergePatientId && !samePatientSelected),

        retry: false
    })

    const mergeJobQuery = useQuery({
        queryKey: ['patient-merge-job', mergeJobId],

        queryFn: async () => {
            return await http.getRequest<PatientMergeJobResponse>(
                `/admin/patient-merge/${mergeJobId}`,
                undefined,
                {clientName: false}
            )
        },

        enabled: Boolean(mergeJobId),

        refetchInterval: (query) => {
            const status = query.state.data?.job?.status

            return status === 'running' || status === 'queued' ? 3000 : false
        }
    })

    // @ts-ignore // TODO: roles are an array in the user interface, but we've received roles as object from the backend
    const isAdmin = constants.ADMIN_ROLES.includes(user?.app_metadata?.roles?.meta?.role) ?? false
    const canMerge = isAdmin || patientMergePermission === 'write'

    const handleStartMerge = async (): Promise<void> => {
        if (!mainPatientId || !mergePatientId || samePatientSelected || !canMerge) {
            return
        }

        setIsMergeStarted(true)

        setMergeStartError(null)

        try {
            const response = await http.postRequest<{ jobId: string }>(
                '/admin/patient-merge',
                {mainPatientId, mergePatientId},
                {clientName: false}
            )

            setMergeJobId(response.jobId)
        } catch (error: any) {
            setMergeStartError(error?.response?.data?.error ?? 'Failed to start merge.')
        } finally {
            setIsMergeStarted(false)
        }
    }

    const resolveClientName = useMemo(() => {
        return (clientKey?: string | null): string => {
            if (!clientKey) {
                return 'Unknown client'
            }

            return clients?.find((client) => client.clientName === clientKey)?.display ?? clientKey
        }
    }, [clients])

    const searchResults = patientSearchQuery.data?.data ?? []
    const isSearchCurrent = searchValue.trim() === debouncedSearch.trim()

    const hasOpenEncounters = constraintsQuery.data?.openEncounters?.blocking ?? false
    const hasDraftIgaos = constraintsQuery.data?.draftIgaos?.warning ?? false

    const isMergeRunning = isMergeStarted || mergeJobQuery.isFetching || mergeJobQuery.data?.job?.status === 'running' || mergeJobQuery.data?.job?.status === 'queued'
    const isMergeCompleted = mergeJobQuery.data?.job?.status === 'completed'

    return (
        <div className='flex flex-col gap-4'>
            <PageHeader className='p-0'>
                <div className='flex items-center gap-4'>
                    <button
                        type='button'
                        onClick={() => {
                            if (props.onBack) {
                                props.onBack()

                                return
                            }

                            window.history.back()
                        }}
                        className='cursor-pointer flex h-11 w-11 items-center justify-center rounded-full bg-[#F2F4F7] text-gray-600 transition hover:bg-gray-200 hover:text-gray-800'
                    >
                        <LuArrowLeft size={22} />
                    </button>

                    <div className='flex flex-col gap-0.5'>
                        <p className='text-2xl font-medium text-[#1C212B]'>Merge Patients</p>

                        <p className='text-sm text-[#6B7280]'>Confirm the primary record and resolve any data conflicts.</p>
                    </div>
                </div>
            </PageHeader>

            <div className='flex flex-col gap-4'>
                <SearchInput
                    placeholder='Search duplicated patients'
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    wrapperClassName='bg-gray-50'
                />

                <div className='overflow-x-auto overflow-hidden rounded-xl bg-gray-50'>
                    <table className='min-w-full text-left text-sm border-collapse'>
                        <tbody>
                            {!searchValue.trim() && (
                                <tr>
                                    <td colSpan={7} className='px-4 py-6 text-center text-gray-400'>
                                        Enter a search above to find patients.
                                    </td>
                                </tr>
                            )}

                            {searchValue.trim() && (!isSearchCurrent || patientSearchQuery.isFetching) && (
                                <tr>
                                    <td colSpan={7} className='px-4 py-6 text-center text-gray-400'>
                                        Searching...
                                    </td>
                                </tr>
                            )}

                            {searchValue.trim() && isSearchCurrent && !patientSearchQuery.isFetching && searchResults.length === 0 && (
                                <tr>
                                    <td colSpan={7} className='px-4 py-6 text-center text-gray-400'>
                                        No matching patients found.
                                    </td>
                                </tr>
                            )}

                            {searchValue.trim() && isSearchCurrent && !patientSearchQuery.isFetching && searchResults.length > 0 && searchResults.map((patient) => { const fullName = `${patient.firstName} ${patient.lastName}`.trim()
                                const clientLabel = resolveClientName(patient._clientName)
                                const isMain = mainPatientId === patient.id
                                const isMerge = mergePatientId === patient.id

                                return (
                                    <tr key={patient.id} className='bg-gray-50 transition hover:bg-gray-100'>
                                        <td className='whitespace-nowrap px-4 py-3'>
                                            <div className='flex items-center gap-2'>
                                                <Link
                                                    to={`/patient/${patient._clientName}/${patient.id}`}
                                                    target='_blank'
                                                    className='font-medium text-indigo-600 hover:text-indigo-800 hover:underline'
                                                >
                                                    {fullName ?? <span className='text-gray-500'>{constants.EMPTY_LABEL}</span>}
                                                </Link>
                                            </div>
                                        </td>

                                        <td className='whitespace-nowrap px-4 py-3'>
                                            {clientLabel}
                                        </td>

                                        <td className='whitespace-nowrap px-4 py-3'>
                                            {patient.birthDate ? (
                                                <FriendlyDate date={patient.birthDate} short/>
                                            ) : (
                                                <span className='text-gray-500'>{constants.EMPTY_LABEL}</span>
                                            )}
                                        </td>

                                        <td className='whitespace-nowrap px-4 py-3'>
                                            {patient.status ?? <span className='text-gray-500'>{constants.EMPTY_LABEL}</span>}
                                        </td>

                                        <td className='whitespace-nowrap px-4 py-3'>
                                            {patient.ssn?.trim() ? patient.ssn : <span className='text-gray-500'>{constants.EMPTY_LABEL}</span>}
                                        </td>

                                        <td className='whitespace-nowrap px-4 py-3'>
                                            {patient.memberId?.trim() ? patient.memberId : <span className='text-gray-500'>{constants.EMPTY_LABEL}</span>}
                                        </td>

                                        <td className='whitespace-nowrap px-4 py-3 text-right'>
                                            <div className='inline-flex overflow-hidden rounded-lg bg-white'>
                                                <button
                                                    type='button'

                                                    onClick={() => {
                                                        if (isMain) {
                                                            setMainPatientId(null)
                                                        } else {
                                                            setMainPatientId(patient.id)

                                                            if (mergePatientId === patient.id) {
                                                                setMergePatientId(null)
                                                            }
                                                        }
                                                    }}

                                                    className={`cursor-pointer px-4 py-2 text-xs font-medium focus:outline-none ${
                                                        isMain
                                                            ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                                                            : 'bg-white text-gray-900 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    Primary
                                                </button>

                                                <button
                                                    type='button'

                                                    onClick={() => {
                                                        if (isMerge) {
                                                            setMergePatientId(null)
                                                        } else {
                                                            setMergePatientId(patient.id)

                                                            if (mainPatientId === patient.id) {
                                                                setMainPatientId(null)
                                                            }
                                                        }
                                                    }}
                                                    className={`cursor-pointer px-4 py-2 text-xs font-medium focus:outline-none ${
                                                        isMerge
                                                            ? 'bg-slate-600 text-white hover:bg-slate-700'
                                                            : 'bg-white text-gray-900 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    Secondary
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {mainPatientId && mergePatientId && !samePatientSelected && summaryQuery.isLoading && (
                <div className='flex flex-col items-center justify-center gap-3 rounded-xl bg-gray-50 py-12'>
                    <p className='text-sm text-gray-500'>Loading merge summary...</p>
                </div>
            )}

            {mainPatientId && mergePatientId && !samePatientSelected && summaryQuery.data?.mainPatient && summaryQuery.data?.mergePatient && !summaryQuery.isLoading && (
                <div className='flex gap-6 pt-4'>
                    <div className='flex flex-col gap-6 shrink-0'>
                        <div className='flex items-center gap-2 w-full'>
                            <div className='flex flex-col gap-2 flex-1'>
                                <div className='flex items-center justify-between px-1'>
                                    <span className='text-xs tracking-wide text-gray-600'>Secondary Record</span>

                                    <span className='rounded bg-red-50 px-2 py-0.5 text-xs font-medium text-red-600'>To Delete</span>
                                </div>

                                <div className='rounded-xl bg-gray-50 p-6 opacity-70 border-2 border-dashed border-gray-200'>
                                    <div className='min-w-0'>
                                        <p className='truncate text-lg font-semibold text-gray-900 pb-1'>
                                            {utils.formatValue(`${(summaryQuery.data.mergePatient.firstName ?? '').trim()} ${(summaryQuery.data.mergePatient.lastName ?? '').trim()}`.trim(), 'Unknown name')}
                                        </p>

                                        <p className='text-sm text-gray-500'>PT-{summaryQuery.data.mergePatient.id}</p>
                                    </div>
                                </div>
                            </div>

                            <div className='flex items-center justify-center pt-8 px-2'>
                                <LuArrowRight className='h-6 w-6 shrink-0 text-gray-300' aria-hidden />
                            </div>

                            <div className='flex flex-col gap-2 flex-1'>
                                <div className='flex items-center justify-between px-1'>
                                    <span className='text-xs tracking-wide text-gray-600'>Primary Record</span>

                                    <span className='rounded bg-green-50 px-2 py-0.5 text-xs font-medium text-green-600'>To Keep</span>
                                </div>

                                <div className='relative rounded-xl bg-gray-50 p-6 border-2 border-solid border-indigo-600'>
                                    <div className='flex items-center justify-center absolute top-0 right-0 px-2 py-1 bg-indigo-600 rounded-lg rounded-tl-none rounded-br-none'>
                                        <span className='text-white text-[0.6rem] font-semibold leading-none uppercase'>Primary</span>
                                    </div>

                                    <div className='min-w-0'>
                                        <p className='truncate text-lg font-semibold text-gray-900 pb-1'>
                                            {utils.formatValue(`${(summaryQuery.data.mainPatient.firstName ?? '').trim()} ${(summaryQuery.data.mainPatient.lastName ?? '').trim()}`.trim(), 'Unknown name')}
                                        </p>

                                        <p className='text-sm text-gray-500'>PT-{summaryQuery.data.mainPatient.id}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='col-span-2 flex flex-col gap-4'>
                            <DataComparison
                                mainPatient={summaryQuery.data.mainPatient}
                                mergePatient={summaryQuery.data.mergePatient}
                                resolveClientName={resolveClientName}
                            />
                        </div>
                    </div>

                    <div className='grow-0'>
                        <MergeSummarySidebar
                            mergeConfirmed={mergeConfirmed}
                            onMergeConfirmedChange={setMergeConfirmed}
                            onConfirmMerge={() => void handleStartMerge()}
                            mergeStartError={mergeStartError}
                            disabled={!mergeConfirmed || hasOpenEncounters || !canMerge || isMergeCompleted || isMergeRunning}
                            hasOpenEncounters={hasOpenEncounters}
                            hasDraftIgaos={hasDraftIgaos}
                            mergeJob={mergeJobQuery.data?.job}
                            isMergeJobRunning={isMergeRunning}
                            isMergeJobCompleted={isMergeCompleted}
                            onBack={props.onBack}
                        />
                    </div>
                </div>
            )}
        </div>
    )
}
